import { SiteInfo, TimeInfo } from './constants/informationText';
import { transformTimezone } from './DataTransform/utils';
import {
  getBusinessClientFormFields,
  getSiteWithBusinessClient,
} from './businessClient';
import {
  siteManageVisitorKey,
  siteRequiresPassKey,
  siteRequiresPhoneKey,
  siteRequiresPhotoIdKey,
  siteRequiresPhotoKey,
  visitorPurposes,
  VISITOR_PASS_OPTIONS,
  VISITOR_PHOTO_OPTIONS,
} from './constants/visitor';
import { getContactData, getContactFieldConfig } from './clientFormOptions';
import get from 'lodash/get';
import { ROLES } from 'utils/constants';
import find from 'lodash/find';
import { LOCATION_MODE } from './constants/Map';
import timezones from 'static/timezones.json';
import { getItemOrDefaultToValue } from './details';

export const siteFields = {
  siteId: {
    field: 'siteId',
    label: 'Site ID',
    type: 'text',
    required: false,
    size: 'half',
    placeholder: 'Enter site id. e.g. SIT-0001',
    info: SiteInfo.siteId,
  },
  siteName: {
    field: 'name',
    type: 'text',
    required: true,
    size: 'half',
    placeholder: 'Enter a site name, e.g. Main Branch',
  },
  timezone: ({ timezones, ...props } = {}) => ({
    field: 'timezone',
    label: 'Time Zone',
    type: 'select',
    required: true,
    size: 'half',
    info: TimeInfo.timezone,
    options: transformTimezone(timezones),
    ...props,
  }),
  businessClient: ({ options = [], disabled = false, ...props }) => ({
    field: 'businessClient',
    type: 'select',
    required: true,
    size: 'half',
    info: SiteInfo.businessClient,
    options,
    disabled,
    ...props,
  }),
  newBusinessClient: {
    field: 'newBusinessClient',
    type: 'toggle',
    size: 'half',
    info: SiteInfo.businessClient,
    alignment: 'center',
  },
  businessClientPanel: ({ addContact, countries, languages, hidden }) => ({
    field: 'newBusinessClientPanel',
    type: 'sub-panel',
    panelHeader: 'New Business Client',
    imageRequired: false,
    subFields: getBusinessClientFormFields(!addContact, countries, languages),
    hidden,
  }),
  contact: ({
    options,
    optionFilterkey,
    selectedOption,
    disabled,
    ...props
  }) => ({
    field: 'contact',
    label: 'Site Contact',
    type: 'select',
    size: 'half',
    info: SiteInfo.contact,
    options: options.filter(
      client => client[optionFilterkey] === selectedOption,
    ),
    disabled,
    ...props,
  }),
  newSiteContact: disabled => ({
    field: 'newSiteContact',
    type: 'toggle',
    size: 'half',
    info: SiteInfo.contact,
    disabled,
    alignment: 'center',
  }),
  siteContactPanel: ({
    countries,
    languages,
    hidden,
    customConfig,
    panelHeader = 'New Site Contact',
  }) =>
    getContactFieldConfig({
      hidden,
      countryOptions: countries,
      languageOptions: languages,
      panelHeader,
      customConfig,
    }),
  manageVisitor: {
    field: siteManageVisitorKey,
    type: 'toggle',
    size: 'half',
    alignment: 'center',
    info: SiteInfo.visitor,
  },
  sitePolicy: ({ hidden }) => ({
    field: 'client',
    type: 'sub-panel',
    panelHeader: 'Site Policy',
    hidden,
    imageRequired: false,
    subFields: [
      {
        field: siteRequiresPassKey,
        type: 'select',
        required: true,
        size: 'half',
        hidden: true,
        options: VISITOR_PASS_OPTIONS,
        info: SiteInfo.pass,
      },
      {
        field: siteRequiresPhotoKey,
        type: 'select',
        required: true,
        size: 'third',
        hidden,
        options: VISITOR_PHOTO_OPTIONS,
        info: SiteInfo.photo,
      },
      {
        field: siteRequiresPhotoIdKey,
        type: 'select',
        required: true,
        size: 'third',
        hidden,
        options: VISITOR_PHOTO_OPTIONS,
        info: SiteInfo.photoId,
      },
      {
        field: siteRequiresPhoneKey,
        type: 'toggle',
        size: 'third',
        hidden,
      },
      {
        field: visitorPurposes,
        type: 'sub-form',
        formHeader: 'visit_purpose_create_button',
        titleKey: 'purpose',
        hidden,
        subFields: [
          {
            field: 'purpose',
            label: 'visit_purpose_label',
            type: 'text',
            required: true,
            maxLength: 50,
            size: 'large',
            placeholder: 'visit_purposes_placeholder',
          },
        ],
        defaultValues: {
          name: '',
        },
        requiredFields: ['purpose'],
      },
    ],
  }),
  address: {
    field: 'address',
    required: true,
    type: 'address',
    info: SiteInfo.address,
    size: 'full',
    placeholder: 'Enter Street Address',
  },
  locations: {
    field: 'locations',
    label: 'Site Boundary',
    required: true,
    type: 'polygon-creator',
    eventType: 'SITE',
    secondaryValue: 'paths',
    additionalPolygonMode: LOCATION_MODE.HIGH_DETAIL,
    displaySites: true,
  },
  siteEmbeddedPanel: ({ newSite, ...props } = {}) => {
    return {
      field: 'newSitePanel',
      type: 'sub-panel',
      panelHeader: 'New Site',
      hidden: !newSite,
      imageRequired: false,
      subFields: getEmbeddedSitePanel(props),
    };
  },
};

export const getEmbeddedSitePanel = ({
  addContact,
  businessClients = [],
  addBusinessClient,
  countries,
  languages,
  clients = [],
  selectedBusinessClient,
  addSiteContact,
  toggleManageVisitor,
  hasVisitorManagement,
  useSelectedValue,
  valueType,
}) => {
  const {
    siteId,
    siteName,
    businessClient: businessClientDropdown,
    newBusinessClient,
    businessClientPanel,
    contact,
    newSiteContact,
    siteContactPanel,
    sitePolicy,
    manageVisitor,
    locations,
    address,
    timezone,
  } = siteFields;
  return [
    siteId,
    { ...siteName },
    businessClientDropdown({
      options: businessClients,
      disabled: addBusinessClient,
      useSelectedValue,
      valueType,
    }),
    newBusinessClient,
    businessClientPanel({
      addContact,
      countries,
      languages,
      hidden: !addBusinessClient,
    }),
    contact({
      options: clients || [],
      optionFilterkey: 'businessClientId',
      selectedOption: selectedBusinessClient,
      disabled: !selectedBusinessClient || addBusinessClient || addSiteContact,
      useSelectedValue,
      valueType,
    }),
    newSiteContact(!selectedBusinessClient || addBusinessClient),
    siteContactPanel({
      hidden: !addSiteContact,
      countries,
      languages,
    }),
    { ...manageVisitor, hidden: !hasVisitorManagement },
    sitePolicy({ hidden: toggleManageVisitor }),
    address,
    timezone({ timezones, size: 'full' }),
    { ...locations, field: 'siteLocations' },
  ];
};

export const transformEmbeddedSiteForm = ({
  values,
  userDetails: { regionId, regionName, securityCompanyId, securityCompanyName },
  businessClients,
  rawClients,
}) => {
  const ids = values?.departments &&
    values?.departments.length > 0 && {
      departmentIds: getItemOrDefaultToValue(
        values,
        'departments',
        undefined,
      )?.map(m => m.id),
    };
  const securityObj = {
    regionId,
    regionName,
    securityCompanyId,
    securityCompanyName,
  };

  if (values.newBusinessClient) {
    return getSiteWithBusinessClient({
      values,
      region: { id: regionId, name: regionName },
      securityCompanyName,
      securityCompanyId,
      generalInfo: { securityCompanyId, securityCompanyName },
    });
  }

  const businessClient =
    typeof values.businessClient === 'object'
      ? values.businessClient.id
      : values.businessClient;

  if (values.newSiteContact) {
    return {
      ...values,
      ...securityObj,
      contactData: getContactData(values, securityObj, ROLES),
      businessClient: find(businessClients, { id: businessClient }),
      ...(ids && { departmentIds: ids.departmentIds }),
    };
  }

  return {
    ...values,
    ...securityObj,
    businessClient: find(businessClients, { id: businessClient }),
    contact: get(find(rawClients, { id: values.contact }), 'value'),
    ...(ids && { departmentIds: ids.departmentIds }),
  };
};
